"use client"

import { useEffect, useRef, useState } from "react"
import { useRouter } from "next/navigation"
import { AnimatePresence, motion } from "framer-motion"
import type { LucideIcon } from "lucide-react"
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input"
import "react-phone-number-input/style.css"
import { z } from "zod"
import {
    ArrowLeft,
    ArrowRight,
    BadgeCheck,
    Banknote,
    BarChart3,
    BookOpen,
    ChevronDown,
    CircleDot,
    DollarSign,
    Dumbbell,
    ExternalLink,
    Eye,
    Flame,
    FlaskConical,
    Globe,
    GraduationCap,
    Hand,
    HeartPulse,
    Info,
    Landmark,
    Loader2,
    MapPin,
    Microscope,
    Monitor,
    Newspaper,
    Pill,
    Scale,
    School,
    ScrollText,
    Sparkles,
    Stethoscope,
    Target,
    ThumbsUp,
    Trash2,
    TrendingUp,
    X,
    Trophy,
    Wheat,
    Wrench,
} from "lucide-react"
import { toast } from "sonner"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import {
    MatchedUniversity,
    StudentProfile,
    universityFinderService,
} from "@/services/university-finder"
import ReactSelect from "react-select"

const MONTH_NAMES = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

const FIELDS: { value: string; label: string; icon: LucideIcon }[] = [
    { value: "CS", label: "Computer Science", icon: Monitor },
    { value: "Engineering", label: "Engineering", icon: Wrench },
    { value: "Business", label: "Business", icon: BarChart3 },
    { value: "Science", label: "Science", icon: FlaskConical },
    { value: "Medicine", label: "Medicine", icon: Stethoscope },
    { value: "Law", label: "Law", icon: Scale },
    { value: "Education", label: "Education", icon: School },
    { value: "Architecture", label: "Architecture", icon: Landmark },
    { value: "Journalism", label: "Journalism", icon: Newspaper },
    { value: "Nursing", label: "Nursing", icon: Pill },
    { value: "Public Health", label: "Public Health", icon: HeartPulse },
    { value: "Agriculture", label: "Agriculture", icon: Wheat },
    { value: "Other", label: "Other", icon: CircleDot },
]

const DEGREE_LEVELS: { value: string; label: string; icon: LucideIcon }[] = [
    { value: "undergrad", label: "Undergraduate", icon: GraduationCap },
    { value: "masters", label: "Masters", icon: ScrollText },
    { value: "phd", label: "PhD", icon: Microscope },
]

const GPA_SCALE_OPTIONS = [
    { value: "4.0", label: "4.0" },
    { value: "5.0", label: "5.0" },
]

const ENGLISH_TESTS = [
    { key: "ielts" as const, label: "IELTS", placeholder: "e.g. 7.0" },
    { key: "toefl" as const, label: "TOEFL", placeholder: "e.g. 100" },
    { key: "duolingo" as const, label: "Duolingo", placeholder: "e.g. 120" },
]

const FUNDING_OPTIONS: { value: string; label: string; icon: LucideIcon }[] = [
    { value: "full", label: "Full Scholarship", icon: DollarSign },
    { value: "partial", label: "Partial OK", icon: Banknote },
]

const EFC_OPTIONS: { label: string; value: string }[] = [
    { label: "< $10K / yr", value: "8000" },
    { label: "$10K–$15K / yr", value: "13000" },
    { label: "$15K–$20K / yr", value: "18000" },
    { label: "$20K–$25K / yr", value: "23000" },
    { label: "$25K–$30K / yr", value: "28000" },
    { label: "$30K+ / yr", value: "35000" },
]

const _flag = (code: string) =>
    code.toUpperCase().split("").map(c => String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65)).join("")

const COUNTRY_OPTIONS: { label: string; value: string }[] = [
    { value: "US", label: `${_flag("US")} United States` },
    { value: "GB", label: `${_flag("GB")} United Kingdom` },
    { value: "CA", label: `${_flag("CA")} Canada` },
    { value: "AU", label: `${_flag("AU")} Australia` },
    { value: "DE", label: `${_flag("DE")} Germany` },
    { value: "FR", label: `${_flag("FR")} France` },
    { value: "NL", label: `${_flag("NL")} Netherlands` },
    { value: "SE", label: `${_flag("SE")} Sweden` },
    { value: "NO", label: `${_flag("NO")} Norway` },
    { value: "FI", label: `${_flag("FI")} Finland` },
    { value: "DK", label: `${_flag("DK")} Denmark` },
    { value: "CH", label: `${_flag("CH")} Switzerland` },
    { value: "BE", label: `${_flag("BE")} Belgium` },
    { value: "IT", label: `${_flag("IT")} Italy` },
    { value: "ES", label: `${_flag("ES")} Spain` },
    { value: "PT", label: `${_flag("PT")} Portugal` },
    { value: "AT", label: `${_flag("AT")} Austria` },
    { value: "IE", label: `${_flag("IE")} Ireland` },
    { value: "NZ", label: `${_flag("NZ")} New Zealand` },
    { value: "JP", label: `${_flag("JP")} Japan` },
    { value: "KR", label: `${_flag("KR")} South Korea` },
    { value: "SG", label: `${_flag("SG")} Singapore` },
    { value: "MY", label: `${_flag("MY")} Malaysia` },
    { value: "IN", label: `${_flag("IN")} India` },
    { value: "CN", label: `${_flag("CN")} China` },
    { value: "TH", label: `${_flag("TH")} Thailand` },
    { value: "PH", label: `${_flag("PH")} Philippines` },
    { value: "ID", label: `${_flag("ID")} Indonesia` },
    { value: "BD", label: `${_flag("BD")} Bangladesh` },
    { value: "PK", label: `${_flag("PK")} Pakistan` },
    { value: "LK", label: `${_flag("LK")} Sri Lanka` },
    { value: "NP", label: `${_flag("NP")} Nepal` },
    { value: "AE", label: `${_flag("AE")} UAE` },
    { value: "SA", label: `${_flag("SA")} Saudi Arabia` },
    { value: "QA", label: `${_flag("QA")} Qatar` },
    { value: "KW", label: `${_flag("KW")} Kuwait` },
    { value: "BH", label: `${_flag("BH")} Bahrain` },
    { value: "TR", label: `${_flag("TR")} Turkey` },
    { value: "RU", label: `${_flag("RU")} Russia` },
    { value: "PL", label: `${_flag("PL")} Poland` },
    { value: "CZ", label: `${_flag("CZ")} Czech Republic` },
    { value: "HU", label: `${_flag("HU")} Hungary` },
    { value: "ZA", label: `${_flag("ZA")} South Africa` },
    { value: "EG", label: `${_flag("EG")} Egypt` },
    { value: "NG", label: `${_flag("NG")} Nigeria` },
    { value: "GH", label: `${_flag("GH")} Ghana` },
    { value: "MX", label: `${_flag("MX")} Mexico` },
    { value: "BR", label: `${_flag("BR")} Brazil` },
    { value: "AR", label: `${_flag("AR")} Argentina` },
]

const WIZARD_STEPS: { icon: LucideIcon; question: string }[] = [
    { icon: Hand, question: "Let's start with you" },
    { icon: TrendingUp, question: "Scores and study plan" },
]

const fadeSlide = {
    initial: { opacity: 0, y: 20, filter: "blur(4px)" },
    animate: { opacity: 1, y: 0, filter: "blur(0px)" },
    exit: { opacity: 0, y: -20, filter: "blur(4px)" },
    transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
}

const usd = (value: number | null) => value == null ? "—" : `$${value.toLocaleString()}`
const pct = (value: number | null) => value == null ? "—" : `${(value * 100).toFixed(0)}%`
const tierColor = (tier: string) => tier === "safe" ? "bg-primary" : tier === "match" ? "bg-amber-500" : "bg-destructive"
const tierLabel = (tier: string) => tier === "safe" ? "Safe" : tier === "match" ? "Match" : "Reach"

function getEnglishScoreBounds(test: "ielts" | "toefl" | "duolingo") {
    if (test === "ielts") {
        return { min: 0, max: 9 }
    }

    if (test === "toefl") {
        return { min: 0, max: 120 }
    }

    return { min: 10, max: 160 }
}

type FieldErrors = Partial<Record<
    | "name"
    | "phone"
    | "institution"
    | "graduationYear"
    | "gpa"
    | "englishScore"
    | "sat"
    | "field"
    | "degreeLevel"
    | "efc",
    string
>>

function ErrorText({ message }: { message?: string }) {
    if (!message) {
        return null
    }

    return <p className="text-xs font-medium text-red-500">{message}</p>
}

const baseSchema = z.object({
    name: z.string().trim().min(2, "Enter your full name."),
    phone: z.string().trim().min(1, "Enter your phone number.").refine(value => isValidPhoneNumber(value), "Enter a valid phone number."),
    institution: z.string().trim().min(2, "Enter your institution name."),
    graduationYear: z.string().trim(),
    gpa: z.string().trim(),
    gpaScale: z.enum(["4.0", "5.0"]),
    englishTest: z.enum(["ielts", "toefl", "duolingo"]),
    englishScore: z.string().trim(),
    sat: z.string().trim(),
    field: z.string().trim(),
    degreeLevel: z.string().trim(),
    efc: z.string().trim(),
})

const finderValidationSchema = baseSchema.superRefine((values, ctx) => {
    const currentYear = new Date().getFullYear()
    const parsedYear = Number(values.graduationYear)
    const parsedGpa = Number(values.gpa)
    const maxScale = Number(values.gpaScale ?? "4.0")

    if (!values.graduationYear) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["graduationYear"], message: "Enter your graduation year." })
    } else if (!Number.isInteger(parsedYear) || parsedYear < currentYear - 15 || parsedYear > currentYear + 10) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["graduationYear"], message: `Use a year between ${currentYear - 15} and ${currentYear + 10}.` })
    }

    if (!values.gpa) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["gpa"], message: "Enter your GPA." })
    } else if (Number.isNaN(parsedGpa) || parsedGpa <= 0 || parsedGpa > maxScale) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["gpa"], message: `GPA must be between 0 and ${maxScale}.` })
    }

    if (values.englishScore) {
        const parsedEnglish = Number(values.englishScore)

        if (Number.isNaN(parsedEnglish)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["englishScore"], message: "Enter a valid score." })
        } else if (values.englishTest === "ielts" && (parsedEnglish < 0 || parsedEnglish > 9)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["englishScore"], message: "IELTS must be between 0 and 9." })
        } else if (values.englishTest === "toefl" && (!Number.isInteger(parsedEnglish) || parsedEnglish < 0 || parsedEnglish > 120)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["englishScore"], message: "TOEFL must be between 0 and 120." })
        } else if (values.englishTest === "duolingo" && (!Number.isInteger(parsedEnglish) || parsedEnglish < 10 || parsedEnglish > 160)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["englishScore"], message: "Duolingo must be between 10 and 160." })
        }
    }

    if (values.sat) {
        const parsedSat = Number(values.sat)
        if (!Number.isInteger(parsedSat) || parsedSat < 400 || parsedSat > 1600) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["sat"], message: "SAT must be between 400 and 1600." })
        }
    }

    if (!values.field) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["field"], message: "Choose a field of study." })
    }

    if (!values.degreeLevel) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["degreeLevel"], message: "Choose a degree level." })
    }

    if (!values.efc) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["efc"], message: "Select your family contribution range (EFC)." })
    }
})

const UF_KEY = "uf_saved"

export default function UniversityFinderPage() {
    const router = useRouter()
    const isFirstMount = useRef(true)
    const [step, setStep] = useState(1)
    const [phase, setPhase] = useState<"wizard" | "results">("wizard")
    const [direction, setDirection] = useState(1)

    const [name, setName] = useState("")
    const [phone, setPhone] = useState<string | undefined>(undefined)
    const [institution, setInstitution] = useState("")
    const [graduationYear, setGraduationYear] = useState("")
    const [gpa, setGpa] = useState("")
    const [gpaScale, setGpaScale] = useState("4.0")
    const [englishTest, setEnglishTest] = useState<"ielts" | "toefl" | "duolingo">("ielts")
    const [englishScore, setEnglishScore] = useState("")
    const [sat, setSat] = useState("")
    const [field, setField] = useState("")
    const [degreeLevel, setDegreeLevel] = useState("")
    const [efc, setEfc] = useState("")
    const [fundingPref, setFundingPref] = useState("full")
    const [competitions, setCompetitions] = useState(false)
    const [publications, setPublications] = useState(false)
    const [travelHistory, setTravelHistory] = useState<string[]>([])
    const [academicBackground, setAcademicBackground] = useState("")
    const [email, setEmail] = useState("")
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})

    // Load saved data after hydration to avoid SSR/client mismatch
    useEffect(() => {
        try {
            const raw = localStorage.getItem(UF_KEY)
            if (!raw) return
            const s = JSON.parse(raw) as Record<string, unknown>
            if (typeof s.name === "string") setName(s.name)
            if (typeof s.phone === "string") setPhone(s.phone)
            if (typeof s.institution === "string") setInstitution(s.institution)
            if (typeof s.graduationYear === "string") setGraduationYear(s.graduationYear)
            if (typeof s.gpa === "string") setGpa(s.gpa)
            if (s.gpaScale === "4.0" || s.gpaScale === "5.0") setGpaScale(s.gpaScale)
            if (s.englishTest === "ielts" || s.englishTest === "toefl" || s.englishTest === "duolingo") setEnglishTest(s.englishTest)
            if (typeof s.englishScore === "string") setEnglishScore(s.englishScore)
            if (typeof s.sat === "string") setSat(s.sat)
            if (typeof s.field === "string") setField(s.field)
            if (typeof s.degreeLevel === "string") setDegreeLevel(s.degreeLevel)
            if (typeof s.efc === "string") setEfc(s.efc)
            if (typeof s.fundingPref === "string") setFundingPref(s.fundingPref)
            if (typeof s.competitions === "boolean") setCompetitions(s.competitions)
            if (typeof s.publications === "boolean") setPublications(s.publications)
            if (Array.isArray(s.travelHistory)) setTravelHistory(s.travelHistory as string[])
            else if (typeof s.travelHistory === "string" && s.travelHistory) setTravelHistory(s.travelHistory.split(",").map((c: string) => c.trim()).filter(Boolean))
            if (typeof s.academicBackground === "string") setAcademicBackground(s.academicBackground)
            if (typeof s.email === "string") setEmail(s.email)
        } catch { /* ignore corrupt data */ }
    }, [])

    // Persist to localStorage on every change — skip the very first run to avoid
    // overwriting saved data with empty defaults before the load effect can hydrate state
    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false
            return
        }
        const data = { name, phone, institution, graduationYear, gpa, gpaScale, englishTest, englishScore, sat, field, degreeLevel, efc, fundingPref, competitions, publications, travelHistory, academicBackground, email }
        localStorage.setItem(UF_KEY, JSON.stringify(data))
    }, [name, phone, institution, graduationYear, gpa, gpaScale, englishTest, englishScore, sat, field, degreeLevel, efc, fundingPref, competitions, publications, travelHistory, academicBackground, email])

    function clearSavedData() {
        localStorage.removeItem("uf_saved")
        setName("")
        setPhone(undefined)
        setInstitution("")
        setGraduationYear("")
        setGpa("")
        setGpaScale("4.0")
        setEnglishTest("ielts")
        setEnglishScore("")
        setSat("")
        setField("")
        setDegreeLevel("")
        setEfc("")
        setFundingPref("full")
        setCompetitions(false)
        setPublications(false)
        setTravelHistory([])
        setAcademicBackground("")
        setEmail("")
        setFieldErrors({})
        toast.success("Saved data cleared.")
    }

    const [showConsent, setShowConsent] = useState(false)
    const [consentData, setConsentData] = useState(false)
    const [consentTerms, setConsentTerms] = useState(false)
    const [consentRemember, setConsentRemember] = useState(false)

    const [results, setResults] = useState<MatchedUniversity[] | null>(null)
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(false)
    const [sortBy, setSortBy] = useState<"score" | "scholarship" | "rank">("score")
    const [selectedUniversity, setSelectedUniversity] = useState<MatchedUniversity | null>(null)

    const resultsRef = useRef<HTMLDivElement>(null)
    const parsedGpa = Number(gpa)
    const maxGpaScale = Number(gpaScale)
    const hasValidGpa = gpa.trim() !== "" && !Number.isNaN(parsedGpa) && parsedGpa > 0 && parsedGpa <= maxGpaScale
    const englishScoreBounds = getEnglishScoreBounds(englishTest)

    function getValidationPayload() {
        return {
            name,
            phone: phone ?? "",
            institution,
            graduationYear,
            gpa,
            gpaScale,
            englishTest,
            englishScore,
            sat,
            field,
            degreeLevel,
            efc,
        }
    }

    function issuesToErrors(issues: z.ZodIssue[]) {
        const nextErrors: FieldErrors = {}

        for (const issue of issues) {
            const fieldName = issue.path[0]
            if (typeof fieldName === "string" && !(fieldName in nextErrors)) {
                nextErrors[fieldName as keyof FieldErrors] = issue.message
            }
        }

        return nextErrors
    }

    function clearError(fieldName: keyof FieldErrors) {
        setFieldErrors(previous => {
            if (!previous[fieldName]) {
                return previous
            }

            const next = { ...previous }
            delete next[fieldName]
            return next
        })
    }

    function validateStep(targetStep: number) {
        const payload = getValidationPayload()
        const result = finderValidationSchema.safeParse(payload)

        if (result.success) {
            setFieldErrors({})
            return true
        }

        const allowedFields = targetStep === 1
            ? new Set<keyof FieldErrors>(["name", "phone", "institution", "graduationYear", "gpa", "englishScore", "sat", "efc"])
            : new Set<keyof FieldErrors>(["field", "degreeLevel"])

        const filteredIssues = result.error.issues.filter(issue => {
            const fieldName = issue.path[0]
            return typeof fieldName === "string" && allowedFields.has(fieldName as keyof FieldErrors)
        })

        const nextErrors = issuesToErrors(filteredIssues)

        setFieldErrors(nextErrors)
        return Object.keys(nextErrors).length === 0
    }

    function validateWizard() {
        const result = finderValidationSchema.safeParse(getValidationPayload())

        if (result.success) {
            setFieldErrors({})
            return { valid: true as const, errors: {} as FieldErrors, issues: [] as z.ZodIssue[] }
        }

        const nextErrors = issuesToErrors(result.error.issues)

        setFieldErrors(nextErrors)
        return { valid: false as const, errors: nextErrors, issues: result.error.issues }
    }

    function getFirstInvalidStep(errors: FieldErrors) {
        const stepOneFields = new Set<keyof FieldErrors>(["name", "phone", "institution", "graduationYear", "gpa", "englishScore", "sat", "efc"])
        return Object.keys(errors).some(fieldName => stepOneFields.has(fieldName as keyof FieldErrors)) ? 1 : 2
    }

    async function handleFind() {
        const validation = validateWizard()

        if (!validation.valid) {
            toast.error("Please fix the highlighted fields.")
            setDirection(-1)
            setStep(getFirstInvalidStep(validation.errors))
            return
        }

        setLoading(true)
        try {
            const profile: StudentProfile = {
                name: name.trim() || null,
                phone: phone ?? null,
                institution: institution.trim() || null,
                graduation_year: graduationYear ? parseInt(graduationYear) : null,
                travel_history: travelHistory.length > 0 ? travelHistory.join(", ") : null,
                gpa: parseFloat(gpa) || null,
                gpa_scale: gpaScale,
                english_test: englishTest,
                sat: sat ? parseInt(sat) : null,
                field: field || null,
                degree_level: degreeLevel || null,
                efc: efc ? parseInt(efc) : null,
                funding_preference: fundingPref || null,
                competitions,
                publications,
                academic_background: academicBackground || null,
                email: email.trim() || null,
            }

            if (englishScore) {
                if (englishTest === "ielts") {
                    profile.ielts = parseFloat(englishScore)
                } else if (englishTest === "toefl") {
                    profile.toefl = parseInt(englishScore)
                } else {
                    profile.duolingo = parseInt(englishScore)
                }
            }

            const [response] = await Promise.all([
                universityFinderService.find(profile),
                new Promise(resolve => setTimeout(resolve, 1000)),
            ])
            setResults(response.matches)
            setTotal(response.total)
            setPhase("results")
        } catch {
            toast.error("Something went wrong.")
        } finally {
            setLoading(false)
        }
    }

    function goNext() {
        if (!validateStep(step)) {
            toast.error("Please fix the highlighted fields.")
            return
        }

        if (step < 2) {
            setDirection(1)
            setStep(current => Math.min(current + 1, 2))
            return
        }
        // Show consent dialog before submitting (skip if user previously chose "remember")
        if (typeof window !== "undefined" && localStorage.getItem("uf_consent_accepted") === "1") {
            void handleFind()
            return
        }
        setConsentData(false)
        setConsentTerms(false)
        setConsentRemember(false)
        setShowConsent(true)
    }

    function goPrev() {
        if (step > 1) {
            setDirection(-1)
            setStep(current => Math.max(current - 1, 1))
        }
    }

    function backToWizard() {
        setPhase("wizard")
        setStep(1)
    }

    const sortedResults = results ? [...results].sort((a, b) => {
        if (sortBy === "scholarship") {
            return (b.scholarship_amount ?? 0) - (a.scholarship_amount ?? 0)
        }
        if (sortBy === "rank") {
            return (a.qs_rank ?? 99999) - (b.qs_rank ?? 99999)
        }
        return b.match_score - a.match_score
    }) : null
    const safeCount = results?.filter(result => result.match_tier === "safe").length ?? 0
    const matchCount = results?.filter(result => result.match_tier === "match").length ?? 0
    const reachCount = results?.filter(result => result.match_tier === "reach").length ?? 0

    if (phase === "wizard") {
        const StepIcon = WIZARD_STEPS[step - 1].icon

        return (
            <div className="relative min-h-screen overflow-hidden bg-background font-landing">

                <div className="pointer-events-none fixed inset-0">
                    <motion.div
                        animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
                        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute -top-32 -right-32 h-[500px] w-[500px] rounded-full bg-primary/[0.04] blur-[100px]"
                    />
                    <motion.div
                        animate={{ x: [0, -20, 0], y: [0, 30, 0] }}
                        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute -bottom-32 -left-32 h-[400px] w-[400px] rounded-full bg-primary/[0.06] blur-[100px]"
                    />
                </div>

                <div className="relative z-10 flex min-h-[calc(100vh-64px)] items-stretch justify-center px-3 py-3 sm:px-4 sm:py-4 lg:px-6 lg:py-6">
                    <div className="flex w-full max-w-[1600px] flex-1">
                        <AnimatePresence mode="wait" custom={direction}>
                            <motion.div
                                key={step}
                                custom={direction}
                                initial={{ opacity: 0, x: direction * 60, scale: 0.97 }}
                                animate={{ opacity: 1, x: 0, scale: 1 }}
                                exit={{ opacity: 0, x: direction * -60, scale: 0.97 }}
                                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] }}
                                className="flex w-full flex-1"
                            >
                                <Card className="relative flex w-full flex-1 rounded-[28px] border-border/50 bg-background/80 shadow-2xl shadow-primary/5 backdrop-blur-sm">
                                    {/* Top-right controls */}
                                    <div className="absolute right-4 top-4 z-20 flex items-center gap-2">
                                        {step === 1 && (
                                            <button
                                                onClick={clearSavedData}
                                                title="Clear saved data"
                                                className="flex items-center gap-1.5 rounded-xl border border-border/50 bg-background/90 px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-destructive/50 hover:text-destructive"
                                            >
                                                <Trash2 className="h-3 w-3" /> Clear saved
                                            </button>
                                        )}
                                        <button
                                            onClick={() => router.back()}
                                            title="Close"
                                            className="flex h-8 w-8 items-center justify-center rounded-xl border border-border/50 bg-background/90 text-muted-foreground transition-colors hover:border-border hover:text-foreground"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                    <CardContent className="p-0">
                                        <div className="grid min-h-[calc(100vh-112px)] grid-cols-1 lg:min-h-[calc(100vh-128px)] xl:grid-cols-[340px_minmax(0,1fr)]">
                                            <motion.div
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.15, duration: 0.3 }}
                                                className="border-b border-border/40 bg-primary/[0.03] px-6 py-8 text-center lg:px-8 lg:py-10 xl:border-b-0 xl:border-r xl:px-10 xl:py-12 xl:text-left"
                                            >
                                                <motion.div
                                                    initial={{ scale: 0, rotate: -30 }}
                                                    animate={{ scale: 1, rotate: 0 }}
                                                    transition={{ delay: 0.2, type: "spring", stiffness: 300, damping: 15 }}
                                                    className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 lg:mx-0"
                                                >
                                                    <StepIcon className="h-7 w-7 text-primary" />
                                                </motion.div>
                                                <div className="space-y-2">
                                                    <p className="text-xs font-semibold tracking-[0.18em] text-primary/70">
                                                        University Finder
                                                    </p>
                                                    <h2 className="text-2xl font-bold text-foreground sm:text-3xl xl:text-[2.25rem] xl:leading-[1.1]">
                                                        {WIZARD_STEPS[step - 1].question}
                                                    </h2>
                                                    <p className="mx-auto max-w-sm text-sm text-muted-foreground xl:mx-0 xl:text-base">
                                                        Fill the essentials and we will shape the shortlist around your profile.
                                                    </p>

                                                </div>
                                            </motion.div>

                                            <div className="flex flex-col px-5 py-5 sm:px-7 sm:py-7 lg:px-8 lg:py-8 xl:px-10 xl:py-10">
                                                {step === 1 && (
                                                    <motion.div {...fadeSlide} className="grid gap-5 lg:grid-cols-[1.2fr_0.8fr] xl:gap-8">
                                                        <div className="grid gap-4 md:grid-cols-2">
                                                            <div className="space-y-2 md:col-span-2">
                                                                <Label className="text-xs font-semibold tracking-wide text-muted-foreground">Full name</Label>
                                                                <Input
                                                                    placeholder="Your full name"
                                                                    value={name}
                                                                    onChange={event => {
                                                                        setName(event.target.value)
                                                                        clearError("name")
                                                                    }}
                                                                    className={cn(
                                                                        "h-12 rounded-xl bg-muted/30 text-base transition-colors focus:bg-background",
                                                                        fieldErrors.name ? "border-red-500 focus-visible:ring-red-500" : "border-border/60"
                                                                    )}
                                                                />
                                                                <ErrorText message={fieldErrors.name} />
                                                            </div>
                                                            <div className="space-y-2 md:col-span-2">
                                                                <Label className="text-xs font-semibold tracking-wide text-muted-foreground">
                                                                    Email <span className="font-normal text-muted-foreground/60">(optional)</span>
                                                                </Label>
                                                                <Input
                                                                    type="email"
                                                                    placeholder="your@email.com"
                                                                    value={email}
                                                                    onChange={event => setEmail(event.target.value)}
                                                                    className="h-12 rounded-xl border-border/60 bg-muted/30 text-base transition-colors focus:bg-background"
                                                                />
                                                            </div>
                                                            <div className="space-y-2">
                                                                <Label className="text-xs font-semibold tracking-wide text-muted-foreground">Phone number</Label>
                                                                <div className={cn(
                                                                    "theme-phone-input rounded-xl border bg-muted/30 px-3 py-1.5 transition-colors focus-within:bg-background",
                                                                    fieldErrors.phone ? "border-red-500" : "border-border/60"
                                                                )}>
                                                                    <PhoneInput
                                                                        international
                                                                        defaultCountry="BD"
                                                                        countryCallingCodeEditable={false}
                                                                        placeholder="Enter phone number"
                                                                        value={phone}
                                                                        onChange={value => {
                                                                            setPhone(value)
                                                                            clearError("phone")
                                                                        }}
                                                                        className="flex items-center gap-2"
                                                                        numberInputProps={{
                                                                            autoComplete: "tel",
                                                                            className: "h-9 w-full border-0 bg-transparent text-base outline-none placeholder:text-muted-foreground",
                                                                        }}
                                                                    />
                                                                </div>
                                                                <ErrorText message={fieldErrors.phone} />
                                                            </div>
                                                            <div className="space-y-2">
                                                                <Label className="text-xs font-semibold tracking-wide text-muted-foreground">Graduation year</Label>
                                                                <Select
                                                                    value={graduationYear}
                                                                    onValueChange={value => {
                                                                        setGraduationYear(value)
                                                                        clearError("graduationYear")
                                                                    }}
                                                                >
                                                                    <SelectTrigger className={cn(
                                                                        "h-12 rounded-xl bg-muted/30 text-base transition-colors focus:bg-background",
                                                                        fieldErrors.graduationYear ? "border-red-500 focus-visible:ring-red-500" : "border-border/60"
                                                                    )}>
                                                                        <SelectValue placeholder="Select year" />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        {Array.from({ length: 26 }, (_, i) => new Date().getFullYear() - 15 + i).map(year => (
                                                                            <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                                <ErrorText message={fieldErrors.graduationYear} />
                                                            </div>
                                                            <div className="space-y-2 md:col-span-2">
                                                                <Label className="text-xs font-semibold tracking-wide text-muted-foreground">Institution</Label>
                                                                <Input
                                                                    placeholder="Last institution (e.g. BUET, Notre Dame)"
                                                                    value={institution}
                                                                    onChange={event => {
                                                                        setInstitution(event.target.value)
                                                                        clearError("institution")
                                                                    }}
                                                                    className={cn(
                                                                        "h-12 rounded-xl bg-muted/30 text-base transition-colors focus:bg-background",
                                                                        fieldErrors.institution ? "border-red-500 focus-visible:ring-red-500" : "border-border/60"
                                                                    )}
                                                                />
                                                                <ErrorText message={fieldErrors.institution} />
                                                            </div>

                                                            {/* Academic background — spans full left column */}
                                                            <div className="space-y-2 md:col-span-2">
                                                                <Label className="text-xs font-semibold tracking-wide text-muted-foreground">Academic background</Label>
                                                                <div className="flex flex-wrap gap-2">
                                                                    {["HSC", "O/A Level", "IB", "Others"].map(option => (
                                                                        <button
                                                                            key={option}
                                                                            onClick={() => setAcademicBackground(prev => prev === option ? "" : option)}
                                                                            className={cn(
                                                                                "rounded-xl border px-4 py-2 text-sm font-semibold transition-all duration-200",
                                                                                academicBackground === option
                                                                                    ? "scale-105 border-primary bg-primary/10 text-primary shadow-sm"
                                                                                    : "border-border/40 bg-muted/30 text-muted-foreground hover:border-primary/30 hover:text-foreground"
                                                                            )}
                                                                        >
                                                                            {option}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </div>

                                                            {/* Background — spans full left column */}
                                                            <div className="space-y-2 md:col-span-2">
                                                                <Label className="text-xs font-semibold tracking-wide text-muted-foreground">Achievement</Label>
                                                                <div className="space-y-2">
                                                                    {[
                                                                        { label: "Competition Winner", sub: "National / international awards", icon: Trophy, checked: competitions, toggle: setCompetitions },
                                                                        { label: "Research Published", sub: "Papers or journals", icon: BookOpen, checked: publications, toggle: setPublications },
                                                                    ].map((item, index) => (
                                                                        <motion.button
                                                                            key={item.label}
                                                                            initial={{ opacity: 0, y: 10 }}
                                                                            animate={{ opacity: 1, y: 0 }}
                                                                            transition={{ delay: index * 0.1 }}
                                                                            onClick={() => item.toggle(!item.checked)}
                                                                            className={cn(
                                                                                "flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-all duration-200",
                                                                                item.checked
                                                                                    ? "border-primary bg-primary/5 shadow-sm"
                                                                                    : "border-border/40 bg-muted/30 hover:border-primary/30"
                                                                            )}
                                                                        >
                                                                            <div className={cn(
                                                                                "flex h-8 w-8 shrink-0 items-center justify-center rounded-xl transition-colors",
                                                                                item.checked ? "bg-primary text-primary-foreground" : "bg-muted"
                                                                            )}>
                                                                                <item.icon className="h-4 w-4" />
                                                                            </div>
                                                                            <div className="min-w-0 flex-1">
                                                                                <div className="text-sm font-semibold">{item.label}</div>
                                                                                <div className="text-xs text-muted-foreground">{item.sub}</div>
                                                                            </div>
                                                                            <div className={cn(
                                                                                "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-all",
                                                                                item.checked ? "border-primary bg-primary" : "border-border"
                                                                            )}>
                                                                                {item.checked && (
                                                                                    <motion.svg initial={{ scale: 0 }} animate={{ scale: 1 }} className="h-2.5 w-2.5 text-primary-foreground" viewBox="0 0 12 12">
                                                                                        <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                                                                    </motion.svg>
                                                                                )}
                                                                            </div>
                                                                        </motion.button>
                                                                    ))}
                                                                </div>
                                                            </div>

                                                            {/* Travel history — spans full left column */}
                                                            <div className="space-y-2 md:col-span-2">
                                                                <Label className="text-xs font-semibold tracking-wide text-muted-foreground flex items-center gap-1.5">
                                                                    <Globe className="h-3.5 w-3.5" /> Countries visited <span className="font-normal opacity-60">(optional)</span>
                                                                </Label>
                                                                <ReactSelect
                                                                    instanceId="uf-countries"
                                                                    isMulti
                                                                    options={COUNTRY_OPTIONS}
                                                                    value={COUNTRY_OPTIONS.filter(o => travelHistory.includes(o.value))}
                                                                    onChange={selected => setTravelHistory(selected ? selected.map(s => s.value) : [])}
                                                                    placeholder="Search countries..."
                                                                    unstyled
                                                                    classNames={{
                                                                        container: () => "text-sm",
                                                                        control: ({ isFocused }) => cn(
                                                                            "min-h-[44px] rounded-xl border px-3 py-1.5 cursor-pointer transition-colors",
                                                                            isFocused ? "border-primary ring-1 ring-primary" : "border-border/60 bg-muted/30 hover:border-border"
                                                                        ),
                                                                        valueContainer: () => "flex flex-wrap gap-1 py-0.5",
                                                                        multiValue: () => "flex items-center gap-0.5 rounded-lg bg-primary/10 border border-primary/20 px-2 py-0.5 text-xs font-semibold text-primary",
                                                                        multiValueLabel: () => "text-primary leading-none",
                                                                        multiValueRemove: () => "text-primary/50 hover:text-primary ml-1 cursor-pointer",
                                                                        menu: () => "mt-1.5 rounded-xl border border-border bg-popover shadow-xl overflow-hidden z-[9999]",
                                                                        menuList: () => "p-1 max-h-[220px] overflow-y-auto",
                                                                        option: ({ isFocused, isSelected }) => cn(
                                                                            "px-3 py-2 rounded-lg text-sm cursor-pointer",
                                                                            isSelected ? "bg-primary text-primary-foreground" : isFocused ? "bg-muted" : "text-foreground"
                                                                        ),
                                                                        placeholder: () => "text-muted-foreground text-sm",
                                                                        input: () => "text-foreground",
                                                                        indicatorsContainer: () => "flex items-center gap-1 pl-2",
                                                                        indicatorSeparator: () => "hidden",
                                                                        clearIndicator: () => "text-muted-foreground hover:text-foreground cursor-pointer p-0.5",
                                                                        dropdownIndicator: () => "text-muted-foreground hover:text-foreground cursor-pointer p-0.5",
                                                                        noOptionsMessage: () => "text-muted-foreground text-sm p-4 text-center",
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="space-y-4 rounded-2xl border border-border/40 bg-muted/20 p-4 sm:p-5">
                                                            <div className="space-y-2">
                                                                <Label className="text-xs font-semibold tracking-wide text-muted-foreground">GPA scale</Label>
                                                                <div className="grid grid-cols-2 gap-2">
                                                                    {GPA_SCALE_OPTIONS.map(option => (
                                                                        <button
                                                                            key={option.value}
                                                                            onClick={() => {
                                                                                setGpaScale(option.value)
                                                                                clearError("gpa")
                                                                            }}
                                                                            className={cn(
                                                                                "rounded-xl px-5 py-2.5 text-sm font-semibold transition-all duration-200",
                                                                                gpaScale === option.value
                                                                                    ? "scale-105 bg-primary text-primary-foreground shadow-lg shadow-primary/25"
                                                                                    : "bg-background text-muted-foreground hover:bg-muted"
                                                                            )}
                                                                        >
                                                                            {option.label} Scale
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                            <div className="space-y-2">
                                                                <Label className="text-xs font-semibold tracking-wide text-muted-foreground">Current GPA</Label>
                                                                <div className="relative">
                                                                    <Input
                                                                        type="number"
                                                                        step="0.01"
                                                                        min="0"
                                                                        max={String(maxGpaScale)}
                                                                        placeholder={gpaScale === "4.0" ? "Your CGPA (e.g. 3.75)" : "Your CGPA (e.g. 4.50)"}
                                                                        value={gpa}
                                                                        onChange={event => {
                                                                            setGpa(event.target.value)
                                                                            clearError("gpa")
                                                                        }}
                                                                        className={cn(
                                                                            "h-20 rounded-xl bg-background text-center text-2xl font-semibold transition-colors focus:bg-background",
                                                                            fieldErrors.gpa ? "border-red-500 focus-visible:ring-red-500" : "border-border/60"
                                                                        )}
                                                                    />
                                                                    {hasValidGpa && (
                                                                        <motion.div
                                                                            initial={{ opacity: 0, scale: 0.8 }}
                                                                            animate={{ opacity: 1, scale: 1 }}
                                                                            className={cn(
                                                                                "absolute -right-2 -top-2 flex h-8 w-8 items-center justify-center rounded-full",
                                                                                parsedGpa >= 3.5 ? "bg-primary" :
                                                                                    parsedGpa >= 3.0 ? "bg-amber-500" : "bg-red-400"
                                                                            )}
                                                                        >
                                                                            {parsedGpa >= 3.5
                                                                                ? <Flame className="h-4 w-4 text-white" />
                                                                                : parsedGpa >= 3.0
                                                                                    ? <ThumbsUp className="h-4 w-4 text-white" />
                                                                                    : <Dumbbell className="h-4 w-4 text-white" />}
                                                                        </motion.div>
                                                                    )}
                                                                </div>
                                                                <ErrorText message={fieldErrors.gpa} />
                                                            </div>

                                                            <div className="space-y-3 border-t border-border/40 pt-4">
                                                                <Label className="text-xs font-semibold tracking-wide text-muted-foreground">English test</Label>
                                                                <div className="flex overflow-hidden rounded-xl border border-border/60 bg-background">
                                                                    {ENGLISH_TESTS.map(test => (
                                                                        <button
                                                                            key={test.key}
                                                                            onClick={() => {
                                                                                setEnglishTest(test.key)
                                                                                clearError("englishScore")
                                                                            }}
                                                                            className={cn(
                                                                                "relative flex-1 py-2.5 text-sm font-semibold transition-all duration-200",
                                                                                englishTest === test.key
                                                                                    ? "text-primary"
                                                                                    : "text-muted-foreground hover:text-foreground"
                                                                            )}
                                                                        >
                                                                            {test.label}
                                                                            {englishTest === test.key && (
                                                                                <motion.div
                                                                                    layoutId="testTab"
                                                                                    className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full bg-primary"
                                                                                />
                                                                            )}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                                <Input
                                                                    type="number"
                                                                    step={englishTest === "ielts" ? "0.5" : "1"}
                                                                    min={String(englishScoreBounds.min)}
                                                                    max={String(englishScoreBounds.max)}
                                                                    placeholder={ENGLISH_TESTS.find(test => test.key === englishTest)?.placeholder}
                                                                    value={englishScore}
                                                                    onChange={event => {
                                                                        setEnglishScore(event.target.value)
                                                                        clearError("englishScore")
                                                                    }}
                                                                    className={cn(
                                                                        "h-12 rounded-xl bg-background text-center text-lg font-semibold transition-colors focus:bg-background",
                                                                        fieldErrors.englishScore ? "border-red-500 focus-visible:ring-red-500" : "border-border/60"
                                                                    )}
                                                                />
                                                                <ErrorText message={fieldErrors.englishScore} />

                                                                <div className="space-y-1.5 border-t border-border/40 pt-4">
                                                                    <Label className="text-xs font-semibold tracking-wide text-muted-foreground">SAT score</Label>
                                                                    <Input
                                                                        type="number"
                                                                        min="400"
                                                                        max="1600"
                                                                        placeholder="e.g. 1350"
                                                                        value={sat}
                                                                        onChange={event => {
                                                                            setSat(event.target.value)
                                                                            clearError("sat")
                                                                        }}
                                                                        className={cn(
                                                                            "h-12 rounded-xl bg-background text-center text-lg font-semibold transition-colors focus:bg-background",
                                                                            fieldErrors.sat ? "border-red-500 focus-visible:ring-red-500" : "border-border/60"
                                                                        )}
                                                                    />
                                                                    <ErrorText message={fieldErrors.sat} />
                                                                </div>

                                                                <div className="space-y-1.5 border-t border-border/40 pt-4">
                                                                    <Label className="text-xs font-semibold tracking-wide text-muted-foreground flex items-center gap-1.5">
                                                                        Family contribution / year (EFC)
                                                                        <Tooltip>
                                                                            <TooltipTrigger asChild>
                                                                                <button type="button" className="inline-flex items-center justify-center rounded-full text-muted-foreground/60 hover:text-primary transition-colors focus:outline-none">
                                                                                    <Info className="h-3.5 w-3.5" />
                                                                                </button>
                                                                            </TooltipTrigger>
                                                                            <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                                                                                <p className="font-semibold mb-1">Estimated Family Contribution (EFC)</p>
                                                                                <p>EFC is the amount your parents/sponsor can invest in your US education per year. It covers tuition, living expenses, insurance, books, and indirect costs. A higher EFC improves your admission chances at some US universities, and you&apos;ll need to show a bank statement when applying.</p>
                                                                            </TooltipContent>
                                                                        </Tooltip>
                                                                    </Label>
                                                                    <div className="grid grid-cols-3 gap-1.5">
                                                                        {EFC_OPTIONS.map(option => (
                                                                            <button
                                                                                key={option.value}
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    setEfc(prev => prev === option.value ? "" : option.value)
                                                                                    clearError("efc")
                                                                                }}
                                                                                className={cn(
                                                                                    "rounded-xl border px-2 py-2 text-xs font-semibold transition-all duration-200 leading-tight",
                                                                                    efc === option.value
                                                                                        ? "scale-105 border-primary bg-primary/10 text-primary shadow-sm"
                                                                                        : "border-border/40 bg-muted/30 text-muted-foreground hover:border-primary/30 hover:text-foreground"
                                                                                )}
                                                                            >
                                                                                {option.label}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                    <ErrorText message={fieldErrors.efc} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                )}

                                                {step === 2 && (
                                                    <motion.div {...fadeSlide} className="space-y-6">
                                                        <div className="space-y-6">
                                                            <div>
                                                                <p className="mb-3 text-center text-xs font-semibold tracking-wide text-muted-foreground lg:text-left">Pick your field</p>
                                                                <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                                                                    {FIELDS.map(item => (
                                                                        <motion.button
                                                                            key={item.value}
                                                                            whileHover={{ scale: 1.05 }}
                                                                            whileTap={{ scale: 0.95 }}
                                                                            onClick={() => {
                                                                                setField(field === item.value ? "" : item.value)
                                                                                clearError("field")
                                                                            }}
                                                                            className={cn(
                                                                                "flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-medium transition-all duration-200",
                                                                                field === item.value
                                                                                    ? "border-primary bg-primary text-primary-foreground shadow-lg shadow-primary/20"
                                                                                    : "border-border/40 bg-muted/30 text-muted-foreground hover:border-primary/40 hover:bg-muted/60"
                                                                            )}
                                                                        >
                                                                            <item.icon className="h-3.5 w-3.5" />
                                                                            {item.label}
                                                                        </motion.button>
                                                                    ))}
                                                                </div>
                                                                <ErrorText message={fieldErrors.field} />
                                                            </div>

                                                            <div>
                                                                <p className="mb-3 text-center text-xs font-semibold tracking-wide text-muted-foreground lg:text-left">Degree level</p>
                                                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                                                    {DEGREE_LEVELS.map(item => (
                                                                        <motion.button
                                                                            key={item.value}
                                                                            whileHover={{ scale: 1.05 }}
                                                                            whileTap={{ scale: 0.95 }}
                                                                            onClick={() => {
                                                                                setDegreeLevel(item.value)
                                                                                clearError("degreeLevel")
                                                                            }}
                                                                            className={cn(
                                                                                "flex min-h-[96px] flex-col items-center justify-center gap-1.5 rounded-xl border px-3 py-4 text-center transition-all duration-200",
                                                                                degreeLevel === item.value
                                                                                    ? "border-primary bg-primary text-primary-foreground shadow-lg shadow-primary/20"
                                                                                    : "border-border/40 bg-muted/30 text-muted-foreground hover:border-primary/40"
                                                                            )}
                                                                        >
                                                                            <item.icon className="h-5 w-5" />
                                                                            <span className="text-sm font-semibold">{item.label}</span>
                                                                        </motion.button>
                                                                    ))}
                                                                </div>
                                                                <ErrorText message={fieldErrors.degreeLevel} />
                                                            </div>

                                                            <div>
                                                                <p className="mb-3 text-center text-xs font-semibold tracking-wide text-muted-foreground lg:text-left">Funding</p>
                                                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                                                    {FUNDING_OPTIONS.map(item => (
                                                                        <motion.button
                                                                            key={item.value}
                                                                            whileHover={{ scale: 1.05 }}
                                                                            whileTap={{ scale: 0.95 }}
                                                                            onClick={() => setFundingPref(item.value)}
                                                                            className={cn(
                                                                                "flex min-h-[84px] flex-col items-center justify-center gap-1 rounded-xl border px-3 py-3 text-center transition-all duration-200",
                                                                                fundingPref === item.value
                                                                                    ? "border-primary bg-primary text-primary-foreground shadow-lg shadow-primary/20"
                                                                                    : "border-border/40 bg-muted/30 text-muted-foreground hover:border-primary/40"
                                                                            )}
                                                                        >
                                                                            <item.icon className="h-5 w-5" />
                                                                            <span className="text-xs font-semibold">{item.label}</span>
                                                                        </motion.button>
                                                                    ))}
                                                                </div>
                                                            </div>

                                                        </div>
                                                    </motion.div>
                                                )}

                                                <div className="mt-auto flex items-center justify-between border-t border-border/40 pt-6">
                                                    {step === 1 ? (
                                                        <div />
                                                    ) : (
                                                        <Button
                                                            variant="ghost"
                                                            onClick={goPrev}
                                                            className="gap-2 rounded-xl opacity-60 transition-opacity hover:opacity-100"
                                                        >
                                                            <ArrowLeft className="h-4 w-4" /> Back
                                                        </Button>
                                                    )}

                                                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                                        <Button
                                                            onClick={goNext}
                                                            disabled={loading}
                                                            size="lg"
                                                            className={cn(
                                                                "min-w-[140px] gap-2 rounded-xl px-8 font-bold transition-all",
                                                                step === 2 && "bg-gradient-to-r from-primary to-primary/80 shadow-lg shadow-primary/25"
                                                            )}
                                                        >
                                                            {loading ? (
                                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                            ) : step === 2 ? (
                                                                <>Find My Matches <Sparkles className="h-4 w-4" /></>
                                                            ) : (
                                                                <>Next <ArrowRight className="h-4 w-4" /></>
                                                            )}
                                                        </Button>
                                                    </motion.div>
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                </div>

            {/* Consent dialog — shown before "Find My Matches" submits */}
            <Dialog open={showConsent} onOpenChange={(open) => { if (!loading) setShowConsent(open) }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-lg font-bold">Before we find your matches</DialogTitle>
                        <DialogDescription className="text-sm text-muted-foreground">
                            To give you a personalised university shortlist, your profile data will be recorded. Our academic advisors may also reach out to offer tailored guidance and callback support.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="mt-2 space-y-4 rounded-xl border border-border/60 bg-muted/30 p-4">
                        <label className="flex cursor-pointer items-start gap-3">
                            <input
                                type="checkbox"
                                checked={consentData}
                                onChange={e => setConsentData(e.target.checked)}
                                className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                            />
                            <span className="text-sm leading-snug text-foreground">
                                I agree that my profile data may be stored and used to personalise my university recommendations and for our team to contact me.
                            </span>
                        </label>
                        <label className="flex cursor-pointer items-start gap-3">
                            <input
                                type="checkbox"
                                checked={consentTerms}
                                onChange={e => setConsentTerms(e.target.checked)}
                                className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                            />
                            <span className="text-sm leading-snug text-foreground">
                                I have read and accept the{" "}
                                <a href="/terms" target="_blank" className="font-medium text-primary underline-offset-2 hover:underline">Terms of Service</a>
                                {" "}and{" "}
                                <a href="/privacy" target="_blank" className="font-medium text-primary underline-offset-2 hover:underline">Privacy Policy</a>.
                            </span>
                        </label>
                    </div>

                    <label className="flex cursor-pointer items-center gap-2.5 px-1 pt-1">
                        <input
                            type="checkbox"
                            checked={consentRemember}
                            onChange={e => setConsentRemember(e.target.checked)}
                            className="h-4 w-4 shrink-0 accent-primary"
                        />
                        <span className="text-xs text-muted-foreground">Remember my choices — don't ask again</span>
                    </label>

                    <DialogFooter className="mt-2 flex gap-2 sm:justify-end">
                        <Button variant="ghost" onClick={() => setShowConsent(false)} className="rounded-xl">
                            Cancel
                        </Button>
                        <Button
                            disabled={!consentData || !consentTerms || loading}
                            onClick={() => {
                                if (consentRemember) localStorage.setItem("uf_consent_accepted", "1")
                                setShowConsent(false)
                                void handleFind()
                            }}
                            className="gap-2 rounded-xl font-bold"
                        >
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4" /> Find My Matches</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-background font-landing">

            <section className="border-b border-border/40 bg-primary/5">
                <div className="container mx-auto px-4 py-4 sm:px-6">
                    <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                        <div>
                            <h1 className="flex items-center gap-2 text-xl font-bold text-foreground">
                                <GraduationCap className="h-5 w-5 text-primary" />
                                <span className="text-primary">{total}</span> universities match your profile
                            </h1>
                            <div className="mt-1 flex gap-4 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-primary" /> {safeCount} safe</span>
                                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500" /> {matchCount} match</span>
                                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-400" /> {reachCount} reach</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-muted-foreground">Sort by</span>
                            <div className="flex gap-1">
                                {([
                                    { key: "score", label: "Match", icon: TrendingUp },
                                    { key: "scholarship", label: "Scholarship", icon: DollarSign },
                                    { key: "rank", label: "QS Rank", icon: Trophy },
                                ] as const).map(item => (
                                    <button
                                        key={item.key}
                                        onClick={() => setSortBy(item.key)}
                                        className={cn(
                                            "flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors",
                                            sortBy === item.key
                                                ? "bg-primary text-primary-foreground"
                                                : "border-border bg-background text-muted-foreground hover:bg-muted"
                                        )}
                                    >
                                        <item.icon className="h-3 w-3" />
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <div className="container mx-auto px-4 py-6 sm:px-6" ref={resultsRef}>
                <div className="grid gap-6 lg:grid-cols-12">
                    <div className="lg:col-span-4 xl:col-span-3">
                        <div className="sticky top-20">
                            <Card className="border-border shadow-sm">
                                <CardContent className="space-y-4 p-4">
                                    <div className="flex items-center gap-2 text-sm font-semibold tracking-wide text-primary">
                                        <div className="h-2 w-2 animate-pulse rounded-full bg-primary" />
                                        Your Profile
                                    </div>

                                    <div className="space-y-1.5">
                                        <Label className="text-xs font-bold tracking-wide text-muted-foreground">CGPA</Label>
                                        <div className="flex gap-2">
                                            <div className="flex overflow-hidden rounded-lg border border-border">
                                                {GPA_SCALE_OPTIONS.map(option => (
                                                    <button
                                                        key={option.value}
                                                        onClick={() => setGpaScale(option.value)}
                                                        className={cn(
                                                            "px-2.5 py-1.5 text-xs font-medium transition-colors",
                                                            gpaScale === option.value ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-muted"
                                                        )}
                                                    >
                                                        {option.label}
                                                    </button>
                                                ))}
                                            </div>
                                            <Input type="number" step="0.01" value={gpa} onChange={event => setGpa(event.target.value)} className="h-8 flex-1 text-sm" />
                                        </div>
                                    </div>

                                    <div className="space-y-1.5">
                                        <Label className="text-xs font-bold tracking-wide text-muted-foreground">English test</Label>
                                        <div className="flex overflow-hidden rounded-lg border border-border">
                                            {ENGLISH_TESTS.map(test => (
                                                <button
                                                    key={test.key}
                                                    onClick={() => setEnglishTest(test.key)}
                                                    className={cn(
                                                        "flex-1 px-2 py-1.5 text-xs font-medium transition-colors",
                                                        englishTest === test.key ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-muted"
                                                    )}
                                                >
                                                    {test.label}
                                                </button>
                                            ))}
                                        </div>
                                        <Input type="number" step={englishTest === "ielts" ? "0.5" : "1"} value={englishScore} onChange={event => setEnglishScore(event.target.value)} className="h-8 text-sm" />
                                    </div>

                                    <div className="space-y-1.5">
                                        <Label className="text-xs font-bold tracking-wide text-muted-foreground">SAT score</Label>
                                        <Input type="number" placeholder="Optional" value={sat} onChange={event => setSat(event.target.value)} className="h-8 text-sm" />
                                    </div>

                                    <div className="space-y-1.5">
                                        <Label className="text-xs font-bold tracking-wide text-muted-foreground">Family contribution / yr</Label>
                                        <div className="grid grid-cols-3 gap-1">
                                            {EFC_OPTIONS.map(option => (
                                                <button
                                                    key={option.value}
                                                    type="button"
                                                    onClick={() => setEfc(prev => prev === option.value ? "" : option.value)}
                                                    className={cn(
                                                        "rounded-lg border px-1.5 py-1.5 text-[10px] font-semibold transition-colors leading-tight",
                                                        efc === option.value
                                                            ? "border-primary bg-primary/10 text-primary"
                                                            : "border-border bg-background text-muted-foreground hover:bg-muted"
                                                    )}
                                                >
                                                    {option.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="space-y-1.5">
                                        <Label className="text-xs font-bold tracking-wide text-muted-foreground">Field</Label>
                                        <Select value={field || "_none"} onValueChange={value => setField(value === "_none" ? "" : value)}>
                                            <SelectTrigger className="h-8 w-full text-sm">
                                                <SelectValue placeholder="Any Field" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="_none">Any Field</SelectItem>
                                                {FIELDS.map(item => (
                                                    <SelectItem key={item.value} value={item.value}>
                                                        <span className="flex items-center gap-2">
                                                            <item.icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                                            {item.label}
                                                        </span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-1.5">
                                        <Label className="text-xs font-bold tracking-wide text-muted-foreground">Degree</Label>
                                        <div className="flex overflow-hidden rounded-lg border border-border">
                                            {DEGREE_LEVELS.map(item => (
                                                <button
                                                    key={item.value}
                                                    onClick={() => setDegreeLevel(item.value)}
                                                    className={cn(
                                                        "flex flex-1 items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium transition-colors",
                                                        degreeLevel === item.value ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-muted"
                                                    )}
                                                >
                                                    <item.icon className="h-3 w-3 shrink-0" />
                                                    {item.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="space-y-1.5">
                                        <Label className="text-xs font-bold tracking-wide text-muted-foreground">Funding</Label>
                                        <div className="flex overflow-hidden rounded-lg border border-border">
                                            {FUNDING_OPTIONS.map(item => (
                                                <button
                                                    key={item.value}
                                                    onClick={() => setFundingPref(item.value)}
                                                    className={cn(
                                                        "flex flex-1 flex-col items-center justify-center gap-0.5 px-2 py-2 text-center transition-colors",
                                                        fundingPref === item.value ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-muted"
                                                    )}
                                                >
                                                    <item.icon className="h-3.5 w-3.5 shrink-0" />
                                                    <div className="text-[10px] font-medium leading-tight">{item.label}</div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <Button onClick={() => void handleFind()} disabled={loading} className="h-9 w-full gap-2 rounded-lg text-sm font-bold">
                                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Update Results <ArrowRight className="h-3.5 w-3.5" /></>}
                                    </Button>
                                </CardContent>
                            </Card>
                        </div>
                    </div>

                    <div className="space-y-4 lg:col-span-8 xl:col-span-9">
                        {sortedResults?.map((university, index) => {
                            return (
                                <motion.div
                                    key={university.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.03, duration: 0.3 }}
                                >
                                    <Card className="overflow-hidden border-border transition-all hover:border-primary/30">
                                        <CardContent className="p-4">
                                            {/* Top row: score bubble + name/meta */}
                                            <div className="flex items-start gap-3">
                                                <div className={cn("flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-full text-xs font-bold text-white", tierColor(university.match_tier))}>
                                                    <span className="text-sm leading-none">{university.match_score}%</span>
                                                </div>

                                                <div className="min-w-0 flex-1">
                                                    <div className="flex flex-wrap items-center gap-1.5">
                                                        <h3 className="text-sm font-bold leading-tight text-foreground sm:text-base">{university.name}</h3>
                                                        <Badge variant="outline" className="text-[10px] font-semibold uppercase">{university.match_tier}</Badge>
                                                    </div>
                                                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                                        {(university.city || university.state) && (
                                                            <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" />{[university.city, university.state].filter(Boolean).join(", ")}</span>
                                                        )}
                                                        {university.qs_rank && <span>QS #{university.qs_rank}</span>}
                                                        {university.deadline_month && university.deadline_year && <span>{MONTH_NAMES[university.deadline_month]} {university.deadline_year}</span>}
                                                    </div>
                                                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                                                        {university.scholarship_type && <Badge variant="secondary" className="text-[10px] font-bold uppercase">{university.scholarship_type} funding</Badge>}
                                                        {university.bangladeshis_admitted && (
                                                            <Badge variant="outline" className="border-primary/30 text-[10px] text-primary">
                                                                <BadgeCheck className="mr-0.5 h-3 w-3" /> BD admitted
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Scholarship amount — desktop only */}
                                                {university.scholarship_amount && (
                                                    <div className="hidden shrink-0 sm:block">
                                                        <div className="rounded-xl border border-primary/30 bg-primary/10 px-3 py-2 text-right">
                                                            <div className="text-xl font-black tracking-tight text-primary">{usd(university.scholarship_amount)}<span className="text-xs font-normal text-primary/70">/yr</span></div>
                                                            <div className="text-[10px] font-medium uppercase tracking-wide text-primary/70">scholarship / aid per year</div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Bottom row: scholarship (mobile) + buttons */}
                                            <div className="mt-3 flex items-center justify-between gap-2 border-t border-border/30 pt-3">
                                                {university.scholarship_amount ? (
                                                    <div className="sm:hidden">
                                                        <div className="rounded-lg border border-primary/30 bg-primary/10 px-2.5 py-1.5">
                                                            <div className="text-base font-black text-primary">{usd(university.scholarship_amount)}<span className="text-xs font-normal text-primary/70">/yr</span></div>
                                                            <div className="text-[10px] font-medium uppercase tracking-wide text-primary/70">scholarship / aid</div>
                                                        </div>
                                                    </div>
                                                ) : <div />}
                                                <div className="flex gap-2 sm:ml-auto">
                                                    {university.apply_url && (
                                                        <a href={university.apply_url} target="_blank" rel="noopener noreferrer">
                                                            <Button size="sm" className="h-8 gap-1 text-xs">Apply Now <ExternalLink className="h-3 w-3" /></Button>
                                                        </a>
                                                    )}
                                                    <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => setSelectedUniversity(university)}>
                                                        <Eye className="h-3 w-3" /> Details
                                                    </Button>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            )
                        })}

                        {sortedResults?.length === 0 && (
                            <div className="py-16 text-center text-muted-foreground">No universities match your profile. Try adjusting your criteria.</div>
                        )}
                    </div>
                </div>
            </div>

            <Dialog open={selectedUniversity != null} onOpenChange={(open) => !open && setSelectedUniversity(null)}>
                <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-4xl">
                    {selectedUniversity && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2 text-xl">
                                    {selectedUniversity.name}
                                    <Badge variant="outline" className="text-xs font-semibold uppercase">{selectedUniversity.match_tier}</Badge>
                                </DialogTitle>
                                <DialogDescription className="text-sm">
                                    {[selectedUniversity.city, selectedUniversity.state].filter(Boolean).join(", ") || "Location unavailable"}
                                </DialogDescription>
                            </DialogHeader>

                            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                                <div className="rounded-xl border border-border bg-muted/20 p-4">
                                    <div className="text-xs font-semibold tracking-wide text-muted-foreground">Match score</div>
                                    <div className="mt-2 text-2xl font-bold text-foreground">{selectedUniversity.match_score}%</div>
                                </div>
                                <div className="rounded-xl border border-border bg-muted/20 p-4">
                                    <div className="text-xs font-semibold tracking-wide text-muted-foreground">Scholarship / year</div>
                                    <div className="mt-2 text-2xl font-bold text-primary">{usd(selectedUniversity.scholarship_amount)}</div>
                                </div>
                                <div className="rounded-xl border border-border bg-muted/20 p-4">
                                    <div className="text-xs font-semibold tracking-wide text-muted-foreground">QS rank</div>
                                    <div className="mt-2 text-2xl font-bold text-foreground">{selectedUniversity.qs_rank ?? "—"}</div>
                                </div>
                                <div className="rounded-xl border border-border bg-muted/20 p-4">
                                    <div className="text-xs font-semibold tracking-wide text-muted-foreground">Deadline</div>
                                    <div className="mt-2 text-2xl font-bold text-foreground">{selectedUniversity.deadline_month && selectedUniversity.deadline_year ? `${MONTH_NAMES[selectedUniversity.deadline_month]} ${selectedUniversity.deadline_year}` : "—"}</div>
                                </div>
                            </div>

                            <div className="grid gap-6 lg:grid-cols-2">
                                <div className="space-y-4">
                                    <div className="rounded-xl border border-border p-4">
                                        <div className="mb-3 text-xs font-semibold tracking-wide text-muted-foreground">Requirements</div>
                                        <div className="grid gap-3 sm:grid-cols-2">
                                            <div>
                                                <div className="text-xs text-muted-foreground">Minimum GPA</div>
                                                <div className="text-sm font-semibold">{selectedUniversity.min_gpa ?? "—"}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-muted-foreground">Minimum IELTS</div>
                                                <div className="text-sm font-semibold">{selectedUniversity.min_ielts ?? "—"}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-muted-foreground">SAT Range</div>
                                                <div className="text-sm font-semibold">{selectedUniversity.sat_25th && selectedUniversity.sat_75th ? `${selectedUniversity.sat_25th}–${selectedUniversity.sat_75th}` : "—"}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-muted-foreground">Acceptance Rate</div>
                                                <div className="text-sm font-semibold">{pct(selectedUniversity.acceptance_rate)}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="rounded-xl border border-border p-4">
                                        <div className="mb-3 text-xs font-semibold tracking-wide text-muted-foreground">Funding & outcomes</div>
                                        <div className="grid gap-3 sm:grid-cols-2">
                                            <div>
                                                <div className="text-xs text-muted-foreground">Scholarship Type</div>
                                                <div className="text-sm font-semibold">{selectedUniversity.scholarship_type ?? "—"}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-muted-foreground">Scholarship Name</div>
                                                <div className="text-sm font-semibold">{selectedUniversity.scholarship_name ?? "—"}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-muted-foreground">Cost Before Aid</div>
                                                <div className="text-sm font-semibold">{usd(selectedUniversity.cost_before)}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-muted-foreground">Cost After Aid</div>
                                                <div className="text-sm font-semibold text-primary">{usd(selectedUniversity.cost_after)}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-muted-foreground">Intl Aid Rate</div>
                                                <div className="text-sm font-semibold">{pct(selectedUniversity.pct_intl_receiving_aid)}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-muted-foreground">Graduation Rate</div>
                                                <div className="text-sm font-semibold">{pct(selectedUniversity.graduation_rate_6yr)}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="rounded-xl border border-border p-4">
                                        <div className="mb-3 text-xs font-semibold tracking-wide text-muted-foreground">Program details</div>
                                        <div className="flex flex-wrap gap-2">
                                            {(selectedUniversity.fields ?? []).length > 0 ? [...new Set(selectedUniversity.fields)].map(fieldName => (
                                                <Badge key={fieldName} variant="secondary" className="text-xs">{fieldName}</Badge>
                                            )) : <span className="text-sm text-muted-foreground">No fields listed.</span>}
                                        </div>
                                        <div className="mt-4 flex flex-wrap gap-2">
                                            {selectedUniversity.bangladeshis_admitted && (
                                                <Badge variant="outline" className="border-primary/30 text-xs text-primary">
                                                    <BadgeCheck className="mr-1 h-3 w-3" /> Bangladeshi students admitted
                                                </Badge>
                                            )}
                                            {selectedUniversity.cds_year && <Badge variant="outline" className="text-xs">CDS {selectedUniversity.cds_year}</Badge>}
                                        </div>
                                    </div>

                                    {selectedUniversity.why_bangladeshi && (
                                        <div className="rounded-xl border border-border p-4">
                                            <div className="mb-3 text-xs font-semibold tracking-wide text-muted-foreground">Why Bangladeshi students choose it</div>
                                            <p className="text-sm leading-6 text-foreground/85">{selectedUniversity.why_bangladeshi}</p>
                                        </div>
                                    )}

                                    <div className="rounded-xl border border-border p-4">
                                        <div className="mb-3 text-xs font-semibold tracking-wide text-muted-foreground">Actions</div>
                                        <div className="flex flex-wrap gap-3">
                                            {selectedUniversity.apply_url && (
                                                <a href={selectedUniversity.apply_url} target="_blank" rel="noopener noreferrer">
                                                    <Button className="gap-2 text-xs">Apply Now <ExternalLink className="h-3 w-3" /></Button>
                                                </a>
                                            )}
                                            {selectedUniversity.scholarship_url && (
                                                <a href={selectedUniversity.scholarship_url} target="_blank" rel="noopener noreferrer">
                                                    <Button variant="outline" className="gap-2 text-xs">Scholarship Info <ExternalLink className="h-3 w-3" /></Button>
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    )
}